/**
 * @lines98/client/lib/daily-server-gate — server-truth гейт «этот аккаунт уже играл
 * доску дня сегодня?» с in-flight дедупом И session-кэшем результата по `dateOfDay`.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-bug 2026-06-11 «двойная игра на 2-м браузере»):
 * idle-гейт доски дня раньше решался ТОЛЬКО по локальному кэшу устройства/снимку — на
 * чистом браузере (другой ПК / очищенный кэш) клиент пускал играть доску, которую ЭТОТ
 * АККАУНТ уже сыграл; сервер отклонял лишь на сабмите (`duplicate_attempt`) → партия
 * впустую. Истина «аккаунт играл сегодня» живёт в серверном `daily_attempts` и выдаётся
 * как `me`-блок `GET /daily/leaderboard` (`lib/daily-final-state.fetchOwnDailyStats`).
 * Этот гейт делает её доступной экрану на idle (фоновой reconcile) И на жёстком клике
 * «Играть доску дня» (await ПЕРЕД стартом) — БЕЗ дубль-запросов к лидерборд-эндпоинту.
 *
 * **Гейт ПОВЕРХ локального, не вместо.** Локальный device-блок (`daily-cache.loadPlayed`)
 * проверяется ПЕРВЫМ и остаётся (тот же браузер не пускает переиграть — owner: «это
 * хорошо»). Этот серверный слой — ДОПОЛНИТЕЛЬНАЯ проверка для случая «тот же аккаунт,
 * другой браузер». Складываются: локальный (устройство) + серверный (аккаунт).
 *
 * **Тонкая ветка над `lib/shared-flight`** (модель «ствол → ветки», code-structure §1):
 * in-flight дедуп держит общий `createSharedFlight` (ключ = `dateOfDay`); здесь — только
 * daily-специфика (`fetchOwnDailyStats` как fetcher) + ДОБАВОЧНЫЙ session-кэш УЖЕ-ресолвнутых
 * результатов (shared-flight чистит in-flight на ресолве — нам нужно ещё помнить ответ в
 * пределах сессии, чтобы навигация пикер↔доска и повторные клики НЕ били по эндпоинту).
 *
 * **Дедуп фон+клик (#п3 дисппатча).** Фоновой idle-reconcile и await-на-клике зовут ОДИН
 * `gateOwnDailyStats(dateOfDay)` → если фон уже в полёте, клик переиспользует ТОТ ЖЕ
 * промис; если фон уже завершился — клик читает session-кэш синхронно (без второго fetch).
 *
 * **Server-truth / анти-чит цел:** это просто более ранний/недублированный легальный
 * `GET /daily/leaderboard` (тот же `me`-блок). Ничего не верифицируется и не пишется в
 * зачёт; «одна попытка/день» по-прежнему — атомарный серверный `daily_attempts` (он же
 * backstop на сабмите). Гейт лишь УБИРАЕТ потраченную впустую партию, сдвигая отказ к
 * старту. Кэш гейта — UX-проекция показа; движок/replayScore его не импортируют.
 *
 * Cross-references:
 *   - lib/shared-flight.ts (ствол: in-flight дедуп по ключу).
 *   - lib/daily-final-state.ts `fetchOwnDailyStats` (источник server-truth `me`-блока).
 *   - screens/daily-board-screen.ts (consumer: idle reconcile + жёсткий гейт на «Играть»).
 *   - packages/server/src/routes/finish-game.ts (атомарный `daily_attempts` — backstop).
 */

import { createSharedFlight } from './shared-flight.js';
import { fetchOwnDailyStats, type OwnDailyStats } from './daily-final-state.js';

// =============================================================================
// In-flight дедуп (ствол) + session-кэш ресолвнутых ответов (ветка-специфика)
// =============================================================================

/**
 * Один полёт `fetchOwnDailyStats` на `dateOfDay` (дедуп фон-reconcile + await-на-клике).
 * shared-flight чистит запись ПОСЛЕ ресолва — поэтому ниже дублируем ответ в `resolved`,
 * чтобы помнить его в пределах сессии (повторные заходы/клики не делают новый запрос).
 */
const flight = createSharedFlight<OwnDailyStats | null>((dateOfDay) =>
  fetchOwnDailyStats(dateOfDay),
);

/**
 * Session-кэш УЖЕ-ресолвнутых результатов по `dateOfDay`. Заполняется на успешном
 * ресолве (см. `gateOwnDailyStats`). НЕ переживает перезагрузку (это in-memory, не
 * localStorage) — намеренно: на reload экран честно ресинкнет server-truth заново.
 * Хранит САМ результат (`OwnDailyStats | null`), чтобы «не играл» (null) тоже кэшировался
 * и не порождал повторных запросов в той же сессии.
 */
const resolved = new Map<string, OwnDailyStats | null>();

/**
 * Server-truth: играл ли ЭТОТ аккаунт доску дня `dateOfDay`?
 *   - запись (`OwnDailyStats`) → сервер: играл (есть `me`-строка в дневном лидерборде);
 *   - `null` → не играл / гость (нет identity → нет `me`) / сетевой сбой.
 *
 * Порядок: session-кэш (синхронно, если уже знаем) → in-flight дедуп (тот же полёт для
 * фон-reconcile и клика) → новый `fetchOwnDailyStats`. Успешный ресолв кэшируется.
 *
 * ВАЖНО про сеть: `fetchOwnDailyStats` возвращает `null` И когда «не играл», И когда
 * сетевой сбой (он не различает). Это намеренно — вызывающий трактует `null` как
 * fail-OPEN (idle / разрешить старт): сервер всё равно backstop-ит на сабмите. Поэтому
 * `null` мы тоже кэшируем как «по нашим данным не играл» — повторный заход в той же
 * сессии не будет долбить эндпоинт; reload честно перепроверит.
 *
 * @param dateOfDay — сегодняшний `date_of_day` (из тёплого seed-кэша / `getDailySeed`).
 */
export function gateOwnDailyStats(dateOfDay: string): Promise<OwnDailyStats | null> {
  // Уже знаем ответ в этой сессии → синхронно, без сети (дедуп навигации/повторных кликов).
  if (resolved.has(dateOfDay)) {
    return Promise.resolve(resolved.get(dateOfDay) ?? null);
  }
  // Иначе — общий in-flight полёт (фон-reconcile и клик переиспользуют один запрос),
  // кэшируем ответ на успешном ресолве.
  return flight.shared(dateOfDay).then((stats) => {
    resolved.set(dateOfDay, stats);
    return stats;
  });
}

/** Test seam: сбросить in-flight дедуп + session-кэш между кейсами (не трогает сеть). */
export function _resetDailyServerGateForTests(): void {
  flight.reset();
  resolved.clear();
}
